/** biome-ignore-all lint/complexity/noThisInStatic: This rule is actually fucked up. `This` and the class name have different targets when the method is inherited. JS static methods are inherited. */
import type { Logger } from "@infra-blocks/logger-interface";
import {
  type ComplexAttributeConverter,
  defaultConverter,
  LitElement,
  type PropertyDeclaration,
  type PropertyDeclarations,
} from "lit";
import type { z } from "zod";
import { checkNotNil } from "../check-not-nil";
import { ConsoleLogger } from "../console-logger";

declare module "lit" {
  /**
  Extending Lit's behavior with extra options.
  */
  interface PropertyDeclaration<Type = unknown, TypeHint = unknown> {
    /**
    The validating schema to protect the property from invalid values.

    When the schema is set, the setter leverages it first to determine if a
    value is valid or not. When invalid, a warning is emitted and the value
    won't be updated, circumventing an update cycle. When valid, the typical
    process continues as expected.

    The schema should never throw on `undefined` or `null` input values.
    Indeed, `null` will be used on the property setter when the matching
    attribute, if any, was removed. `undefined` is considered the default
    value of any property, even when set in the constructor. This is simpler
    than trying to enforce that every property be set with a defined value
    in the subclass' constructor.

    Instead, either the property schema accepts and tolerates both values,
    in which case the type of the property is effectively null | undefined | T,
    or the property schema provides a default value, in which case the type
    of the property is T.

    When the value is invalid and an attribute reflection is requested, the
    current code bypasses Lit's reflection mechanism. The typical Lit reflection
    mechanism occurs during the `update` lifecycle method:
    attributes that were updated previous to the phase are reflected
    ***unless*** an attribute change was the source of their value change.
    In other words, if a property update resulted from its attribute being
    updated, then it won't be reflected back during the update cycle.

    This is problematic for us, as we want to roll back the original value
    into the attribute *only* when the value update was initiated
    by the attribute change. Therefore, we use the `setAttribute` API directly,
    which could trigger re-entrancy, but Lit guards from that scenario
    (attribute => setter => attribute).

    In order to perform the writeback only when the attribute change triggered
    the setter, we keep an additional tracker in class instances. This is
    done so we simply leave the attribute alone when the invalid value
    was provided through the setter. See `attributeChangedCallback`.

    The extension also detects if a default value is assigned to the propety
    by testing against `undefined`. If the result of a parse is not nil, then
    we assume the property has a default value.

    The default value is injected in the getter such that, if the field is
    accessed before it was properly set, by any mechanism, then the default
    value is returned instead.

    For coherence, the default value should also be returned when `null` is
    passed, as this will be the case when the user removes the matching
    attribute, if any. In that scenario, the schema is expected to produce
    the same result as if it was called with undefined.
    */
    // TODO: type the return of the property constructors and use that here,
    // instead of the generic ZodType + warnings and considerations in the
    // doc.
    schema?: z.ZodType<Type>;
  }
}

export type TagName = `wm-${string}`;

export abstract class WmElement extends LitElement {
  protected static get TAG_NAME(): TagName {
    throw new Error(
      "implementors must define the TAG_NAME static readonly property",
    );
  }

  // Redefined here with extended type definition.
  declare static properties: PropertyDeclarations;

  /**
  Extends Lit's own implementations with additional logic described in
  {@link PropertyDeclaration}.
  */
  protected static override getPropertyDescriptor(
    name: PropertyKey,
    key: string | symbol,
    options: PropertyDeclaration,
  ): PropertyDescriptor | undefined {
    // It's unclear when reading the Lit code why this would ever be undefined.
    const defaultDescriptor = checkNotNil(
      super.getPropertyDescriptor(name, key, options),
    );
    const { schema } = options;
    if (schema == null) {
      return defaultDescriptor;
    }

    // We always expect the property schema to be able to parse undefined.
    const defaultValue = schema.parse(undefined);
    const withDefaultValue = defaultValue != null;
    // Technically, we should check that undefined and null default to the
    // same value, otherwise there might be a misunderstanding in client
    // code as to what "default" means.

    // Not sure what to do if the getter would be null in the case of
    // reflecting the attribute back. This constraint could be alleviated,
    // but we can't reflect back the attribute if we can't get the previous
    // value...
    const defaultGetter = checkNotNil(defaultDescriptor.get);
    const getter = withDefaultValue
      ? function (this: WmElement) {
          return defaultGetter.call(this) ?? defaultValue;
        }
      : defaultGetter;
    const setter = checkNotNil(defaultDescriptor.set);
    return {
      get(this: WmElement) {
        return getter.call(this);
      },
      set(this: WmElement, value: unknown) {
        const result = schema.safeParse(value, { reportInput: true });
        if (!result.success) {
          this.logger.warn(
            `invalid value "%s" for "${String(name)}", ignoring`,
            value,
            result.error,
          );
          // Because Lit's optimizes away reflections that would occur as
          // a result of attribute changes, we must perform the reflection
          // manually to keep the attribute in sync.
          //
          // There is a re-entrancy issue arising from setting the attribute
          // within the setter, but Lit already guards against that case.
          //
          // When the setter is invoked directly, then the instance changed
          // attribute tracker will not have a matching value.
          if (options.reflect) {
            const attributeName = WmElement.#attributeNameForProperty(
              name,
              options,
            );
            if (attributeName == null) {
              this.logger.warn(
                "unable to reflect back invalid value as matching attribute name is undefined",
              );
              return;
            }

            // If the setter was called directly instead of through an attribute
            // change, then there is no reflection necessary.
            if (attributeName !== this.changedAttribute) {
              return;
            }

            // Immediately change the attribute back to the previous value.
            // NOTE: we could follow similar semantics as Lit and reflect
            // the value back during the update cycle, but that would require
            // to maintain additional state and partially duplicate the logic
            // that exists in Lit. This is simpler for now, and if it proves
            // problematic, we can look into doing the other implementation.
            const oldValue = getter.call(this);
            const attributeValue = WmElement.#attributeValueForProperty(
              oldValue,
              options,
            );
            if (attributeValue == null) {
              this.removeAttribute(attributeName);
            } else {
              this.setAttribute(attributeName, attributeValue);
            }
          }

          return;
        }

        return setter.call(this, result.data);
      },
      configurable: true,
      enumerable: true,
    };
  }

  // Taken from Lit's own implementation.
  static #attributeNameForProperty(
    name: PropertyKey,
    options: PropertyDeclaration,
  ) {
    const attribute = options.attribute;
    return attribute === false
      ? undefined
      : typeof attribute === "string"
        ? attribute
        : typeof name === "string"
          ? name.toLowerCase()
          : undefined;
  }

  // Taken from Lit's own implementation.
  static #attributeValueForProperty(
    value: unknown,
    options: PropertyDeclaration,
  ) {
    const converter =
      (options.converter as ComplexAttributeConverter)?.toAttribute !==
      undefined
        ? (options.converter as ComplexAttributeConverter)
        : defaultConverter;
    return checkNotNil(converter.toAttribute)(value, options.type) as string;
  }

  protected readonly logger: Logger;
  /**
  This tracker allows us to determine whether a setter was called as a
  result of an attribute update, or whether it was called directly.

  Avoids useless attribute writebacks on invalid data.
  */
  protected changedAttribute?: string;

  constructor() {
    super();
    const ctor = this.constructor as typeof WmElement;
    this.logger = ConsoleLogger.create({
      prefix: `${ctor.TAG_NAME}::`,
    });
  }

  override attributeChangedCallback(
    name: string,
    old: string | null,
    value: string | null,
  ): void {
    this.changedAttribute = name;
    try {
      super.attributeChangedCallback(name, old, value);
    } finally {
      this.changedAttribute = undefined;
    }
  }

  protected query<K extends keyof HTMLElementTagNameMap>(
    selectors: K,
  ): HTMLElementTagNameMap[K] | null;
  protected query<K extends keyof SVGElementTagNameMap>(
    selectors: K,
  ): SVGElementTagNameMap[K] | null;
  protected query<K extends keyof MathMLElementTagNameMap>(
    selectors: K,
  ): MathMLElementTagNameMap[K] | null;
  protected query<E extends Element = Element>(selector: string): E | null;
  protected query(selector: string): Element | null {
    return this.renderRoot.querySelector(selector);
  }

  protected queryNotNil<K extends keyof HTMLElementTagNameMap>(
    selectors: K,
  ): HTMLElementTagNameMap[K];
  protected queryNotNil<K extends keyof SVGElementTagNameMap>(
    selectors: K,
  ): SVGElementTagNameMap[K];
  protected queryNotNil<K extends keyof MathMLElementTagNameMap>(
    selectors: K,
  ): MathMLElementTagNameMap[K];
  protected queryNotNil<E extends Element = Element>(selector: string): E;
  protected queryNotNil(selector: string): Element {
    return checkNotNil(this.query(selector));
  }
}
